import classnames from 'classnames';
import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { toast } from 'react-toastify';
import BackgroundImages from 'v2/common/Background/BackgroundImages';
import { DataFileEntity } from 'v2/DataFiles/models';
import downloadIcon from 'v2/icons/download-cloud.svg';
import { DataProviderEntity, StrapiApiError } from 'v2/models';
import { downloadFile } from 'v2/utils/fileUtils';
import restFactory from 'v2/utils/restFactory';
import DataFilesTable from '../../../DataFiles/DataFilesTable/DataFilesTable';
import DataFileDetails from '../DataFileDetails/DataFileDetails';
import styles from './buyerFileLibrary.module.scss';
import DataProviderDropDown from './DataProviderDropDown';

export type DataBuyerProviderFileLibrary = Pick<
  DataProviderEntity,
  'id' | 'name' | 'avatar'
>;

const BuyerFileLibrary = () => {
  const [selectedProvider, setSelectedProvider] =
    useState<DataBuyerProviderFileLibrary | null>(null);

  const [selectedDataFile, setSelectedDataFile] =
    useState<DataFileEntity | null>(null);

  const { data: dataProviders, isLoading: isLoadingProviders } = useQuery(
    'dataProviders',
    async () => {
      const response = await restFactory.get<DataBuyerProviderFileLibrary[]>(
        'data-providers',
        { _sort: 'name:desc' }
      );
      setSelectedProvider(response[0]);
      return response;
    },
    {
      onError: (error: StrapiApiError) => {
        toast.error(`Error loading data providers. ${error.message || ''}`);
      },
      refetchOnWindowFocus: false,
      retry: false,
    }
  );

  const { data: dataFiles } = useQuery(
    ['dataFiles', selectedProvider],
    async () => {
      const response = await restFactory.get<DataFileEntity[]>('data-files', {
        data_provider: selectedProvider?.id,
      });
      return response;
    },
    {
      enabled: !!selectedProvider,
      onError: (error: StrapiApiError) => {
        toast.error(`Error loading data files. ${error.message}`);
      },
      refetchOnWindowFocus: false,
      retry: false,
    }
  );

  const renderNodataMsg = () => {
    if (!isLoadingProviders) {
      return (
        <h2 className={styles.noDataMsg}>
          There are no files shared with you.
        </h2>
      );
    }
    return null;
  };

  return (
    <div
      className={classnames(
        'content_container p-0',
        styles.root,
        'container-xl px-5'
      )}
    >
      <div className="mt-5 fix-bg-images">
        <BackgroundImages />
        <h1 className="m-0 mb-3">File Library</h1>
      </div>

      <div className={classnames(styles.boxContainer, 'fix-bg-images')}>
        <div
          className={classnames(styles.left, 'scroll', {
            'd-flex align-items-center justify-content-center':
              !dataProviders?.length,
          })}
        >
          {dataProviders?.length ? (
            <DataProviderDropDown
              list={dataProviders}
              selectedProvider={selectedProvider}
              onChange={(data) => {
                if (selectedProvider?.id !== data.id) {
                  setSelectedDataFile(null);
                }
                setSelectedProvider(data);
              }}
            />
          ) : (
            renderNodataMsg()
          )}
          {dataFiles?.length ? (
            <DataFilesTable
              className={styles.table}
              list={dataFiles}
              showFilesCounter={false}
              actions={(item) => (
                <div className={styles.actionContainer}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      downloadFile(item.id);
                    }}
                    aria-label="downloadDataFile"
                  >
                    <img src={downloadIcon} alt="downloadFile"></img>
                  </button>
                </div>
              )}
              selectedRow={selectedDataFile}
              setSelectedRow={setSelectedDataFile}
            />
          ) : null}
        </div>
        <div className={classnames(styles.right, 'scroll')}>
          <DataFileDetails
            data={selectedDataFile}
            onDeselect={() => setSelectedDataFile(null)}
            onDownloadFile={downloadFile}
          />
        </div>
      </div>
    </div>
  );
};

export default BuyerFileLibrary;
